import { ol_Feature, ol_FeatureLike, ol_Map, ol_MapBrowserPointerEvent, ol_style } from "@omnia/exports/openlayers"
import { arrayMax, FieldViewModel, Geometry2, ISoilAnalysisAlloc, Layer, observeOpenlayersObject, selectMany } from "../../../omnia"
import { IDataLayer, IDataLayerZone, LayerTypeId } from "../../../types"
import { GlobalAnalysisAlloc } from "../viewmodel/alloc-vm"
import { GlobalAnalysisVM } from "../viewmodel/analysis-vm"
import { GlobalHealthySoilsAssessmentVM } from "../viewmodel/hsa-vm"
import { GlobalNitrogenAnalysisVM } from "../viewmodel/nitrogen-vm"
import { GlobalSoilAnalysisAlloc } from "../viewmodel/soilanalysis-alloc-vm"
import { GlobalSoilAnalysisVM } from "../viewmodel/soilanalysis-vm"

export interface IAnalysisLayerData {
    analysis: GlobalAnalysisVM;
    field: FieldViewModel;
    zone?: IDataLayerZone;
    layer?: IDataLayer;
    layerType?: LayerTypeId;
}

export interface ISoilAnalysisSource {
    id: any;
    boundary: Geometry2;
    zone: IDataLayerZone;
    field: FieldViewModel;
    layer: IDataLayer;
    layerType: LayerTypeId;
    analysis: GlobalSoilAnalysisVM;
    allocation: GlobalSoilAnalysisAlloc;
}

export class AnalysisLayer extends Layer {

    private _watchers: ((selectedFeatures: any[]) => void)[] = [];
    private selectedFeatures: ol_Feature[];

    constructor(private map: ol_Map, private canSelect: (f: ol_Feature) => boolean) {
        super();
        this.selectedFeatures = [];
    }

    public watchSelection(watcher: (features: ol_Feature[]) => void): Function {

        if (!this._watchers.find(w => w === watcher)) {
            this._watchers.push(watcher);
        }

        return () => {
            this.unwatchSelection(watcher);
        }

    }

    private unwatchSelection(watcher: (features: ol_Feature[]) => void) {
        this._watchers.remove(w => w === watcher);
    }

    private setWatchSelection(features: ol_Feature[]) {

        observeOpenlayersObject(this.map, "click",
            (e: ol_MapBrowserPointerEvent) => {

                let match: ol_FeatureLike

                this.map.forEachFeatureAtPixel(e.pixel,
                    (feature, olayer) => {

                        let layer = feature.get("layer");

                        if (layer instanceof AnalysisLayer) {
                            match = feature;
                            return true;
                        }

                        return false;
                    });

                if (match && (match instanceof ol_Feature)) {
                    this.selectFeature(match);
                }

            });

    }

    private updateSelection() {
        var selection = this.selectedFeatures.slice();
        this._watchers.forEach(w => w(selection));
    }

    public silentUnselectFeature(feature: ol_Feature) {
        this.selectedFeatures.remove(f => f === feature);
        feature.changed();
    }

    private selectFeature(feature: ol_Feature) {

        if (!this.canSelect(feature)) {
            return;
        }

        if (this.selectedFeatures.indexOf(feature) !== -1) {
            this.selectedFeatures.remove(f => f === feature);
        } else {
            this.selectedFeatures.push(feature);
        }

        feature.changed();
        this.updateSelection();
    }

    public selectFeatures(features: ol_Feature[]) {

        features.forEach(f => {
            this.selectedFeatures.push(f);
            f.changed();
        });

        this.updateSelection();

    }

    public clearSelection() {

        var features = this.selectedFeatures.slice();

        this.selectedFeatures = [];

        features.forEach(f => f.changed());

    }

    getLayerStyleFunction(selected: boolean): (feature, resolution) => any {

        var stroke = [new ol_style.Style({
            stroke: new ol_style.Stroke({
                color: "#444",
                width: 1,
                lineDash: [3, 3]
            })
        })];

        var selectedStroke = [
            new ol_style.Style({
                stroke: new ol_style.Stroke({
                    color: 'rgb(36, 96, 240)',
                    width: 5
                })
            }),
            new ol_style.Style({
                stroke: new ol_style.Stroke({
                    color: 'rgb(255, 255, 255)',
                    width: 1
                })
            })
        ];

        var self = this;

        return (feature, resolution) => {

            var data: IAnalysisLayerData = feature.get("data");
            var color = data.analysis ? this.createAnalysisPattern("rgba(0, 0, 0, 0.5)") : [255, 255, 255, 0];
            var selectedColor = this.createAnalysisPattern("rgba(36, 96, 240, 1)", true);
            var text = (resolution <= 5) && self.getFeatureLabelVisibility(feature);

            var isSelected = this.selectedFeatures.indexOf(feature) !== -1;

            return this.createStyle(feature, resolution, color, selectedColor, text, isSelected, stroke, selectedStroke);

        }
    }

    createStyle(feature, resolution, color, selectedColor, includeText, selected, stroke, selectedStroke) {

        var data = <IAnalysisLayerData>feature.get("data");
        if (!data) return;

        var styles = [new ol_style.Style({

            fill: new ol_style.Fill({ color: selected ? selectedColor : color })

        })];

        return styles.concat(selected ? selectedStroke : stroke);

    }

    private createAnalysisPattern(strokeColor: string, selected: boolean = false) {

        const width = 10;
        const height = 10;

        var cnv = document.createElement("canvas");
        var ctx = cnv.getContext("2d");
        cnv.width = width;
        cnv.height = height;

        ctx.fillStyle = "rgba(255,255,255,0)";
        ctx.fillRect(0, 0, width, height);

        ctx.strokeStyle = strokeColor;
        ctx.beginPath();

        if (selected) {
            ctx.moveTo(width, 0);
            ctx.lineTo(0, 0);
            ctx.lineTo(0, height);
        } else {
            ctx.moveTo(0, 0);
            ctx.lineTo(width, height);
        }

        ctx.stroke();

        return ctx.createPattern(cnv, 'repeat');

    }

    public setSoilAnalysis(fields: FieldViewModel[],
        soilAnalysisLayers: IDataLayer[],
        soilTypeLayers: IDataLayer[],
        analysisList: GlobalSoilAnalysisVM[],
        onlyFields: boolean) {

        var source = this.getSoilAnalysisSource(fields, soilAnalysisLayers, soilTypeLayers, analysisList, onlyFields);

        var features = <ol_Feature[]>this.setSource(source, s => s.boundary, s => s.id, s => {

            var data = <IAnalysisLayerData>{
                analysis: s.analysis,
                field: s.field,
                zone: s.zone,
                layer: s.layer,
                layerType: s.layerType
            };

            if (s.allocation) {
                s.allocation.layerData = data;
                s.allocation.shown = true;
            }

            return data;

        });

        this.setWatchSelection(features);
    }

    getSoilAnalysisSource(fields: FieldViewModel[],
        soilAnalysisLayers: IDataLayer[],
        soilTypeLayers: IDataLayer[],
        analysisList: GlobalSoilAnalysisVM[],
        onlyFields: boolean) {

        analysisList.forEach(a => a.allocations.forEach(l => l.shown = false));

        return selectMany(fields.map(f => {

            if (!f.hasBoundary()) {
                return [];
            }

            var fieldSoilAnalysisLayers: IDataLayer[] = soilAnalysisLayers.filter(l => l.FieldId === f.fieldId)
                .sort((a, b) => a.Date > b.Date ? 1 : a.Date < b.Date ? -1 : 0);
            var fieldSoilAnalysisLayer = fieldSoilAnalysisLayers.length ? fieldSoilAnalysisLayers[0] : null;

            var fieldSoilTypeLayers = soilTypeLayers.filter(l => l.FieldId === f.fieldId)
                .sort((a, b) => a.Date > b.Date ? 1 : a.Date < b.Date ? -1 : 0);
            var fieldSoilTypeLayer = fieldSoilTypeLayers.length ? fieldSoilTypeLayers[0] : null;

            var layer = onlyFields ? null : fieldSoilAnalysisLayer ? fieldSoilAnalysisLayer : fieldSoilTypeLayer;

            if (layer) {
                return layer.Zones.map(z => {

                    var zoneAnalysis: GlobalSoilAnalysisVM = null;
                    var zAllocation: GlobalSoilAnalysisAlloc = null;

                    if (fieldSoilAnalysisLayer) {

                        var zAnalysis = analysisList.filter(a =>
                            a.allocations.filter(l =>
                                l.allocation &&
                                (<ISoilAnalysisAlloc>l.allocation).FieldId === f.fieldId &&
                                (<ISoilAnalysisAlloc>l.allocation).ZoneAlloc &&
                                (<ISoilAnalysisAlloc>l.allocation).ZoneId === z.DataLayerZoneId).length > 0);

                        if (zAnalysis.length) {
                            zoneAnalysis = zAnalysis[0];

                            zAllocation = <GlobalSoilAnalysisAlloc>zoneAnalysis.allocations.filter(l =>
                                l.allocation &&
                                (<ISoilAnalysisAlloc>l.allocation).FieldId === f.fieldId &&
                                (<ISoilAnalysisAlloc>l.allocation).ZoneAlloc &&
                                (<ISoilAnalysisAlloc>l.allocation).ZoneId === z.DataLayerZoneId)[0];
                            zAllocation.zone = z;
                            zAllocation.field = f;
                        }

                    }

                    return <ISoilAnalysisSource>{
                        id: f.fieldId + " " + z.DataLayerZoneId,
                        boundary: z.Geometry,
                        zone: z,
                        field: f,
                        layer: layer,
                        layerType: fieldSoilAnalysisLayer ? LayerTypeId.SoilAnalysis : LayerTypeId.SoilType,
                        analysis: zoneAnalysis,
                        allocation: zAllocation
                    };
                });
            }

            var allocatedAnalysis: GlobalSoilAnalysisVM[] = analysisList
                .filter(a => a.allocations.filter(l => l.getId() === f.fieldId).length > 0);
            var fieldAnalysis: GlobalSoilAnalysisVM = null;

            if (allocatedAnalysis.length) {

                var maxDate = arrayMax(allocatedAnalysis, a => a.analysis.SamplingDate);
                fieldAnalysis = allocatedAnalysis.filter(a => a.analysis.SamplingDate === maxDate)[0];

            }

            return [
                <ISoilAnalysisSource>{
                    id: f.fieldId,
                    boundary: f.getBoundary(),
                    zone: null,
                    field: f,
                    layer: null,
                    layerType: null,
                    analysis: fieldAnalysis,
                    allocation: fieldAnalysis ? fieldAnalysis.allocations.filter(l => l.getId() === f.fieldId)[0] : null
                }
            ];

        }));

    }

    public setHealthySoilsAssessment(fields: FieldViewModel[],
        analysisList: GlobalHealthySoilsAssessmentVM[]) {

        var features = <ol_Feature[]>this.setSource(fields, f => f.getBoundary(), f => f.getId(), f => {

            var allocatedHsa: GlobalHealthySoilsAssessmentVM[] = analysisList
                .filter(a => a.allocations.filter(l => l.getId() === f.fieldId).length > 0);
            var fieldHsa: GlobalHealthySoilsAssessmentVM = null;
            var fieldAllocation: GlobalAnalysisAlloc = null;

            if (allocatedHsa.length) {

                var maxDate = arrayMax(allocatedHsa, a => a.analysis.SamplingDate);
                fieldHsa = allocatedHsa.filter(a => a.analysis.SamplingDate === maxDate)[0];

                fieldAllocation = fieldHsa ? fieldHsa.allocations.filter(l => l.getId() === f.fieldId)[0] : null;
            }

            var data = <IAnalysisLayerData>{
                analysis: fieldHsa,
                field: f
            };

            if (fieldAllocation) {
                fieldAllocation.layerData = data;
                fieldAllocation.shown = true;
            }

            return data;

        });

        this.setWatchSelection(features);
    }

    public setNitrogenAnalysis(croppings: FieldViewModel[],
        analysisList: GlobalNitrogenAnalysisVM[]) {

        var features = <ol_Feature[]>this.setSource(croppings, f => f.getBoundary(), f => f.getId(), f => {

            var allocatedAnalysis: GlobalNitrogenAnalysisVM[] = analysisList
                .filter(a => a.allocations.filter(l => l.getId() === f.croppingId).length > 0);
            var croppingAnalysis: GlobalNitrogenAnalysisVM = null;
            var croppingAllocation: GlobalAnalysisAlloc = null;

            if (allocatedAnalysis.length) {

                var maxDate = arrayMax(allocatedAnalysis, a => a.analysis.SamplingDate);
                croppingAnalysis = allocatedAnalysis.filter(a => a.analysis.SamplingDate === maxDate)[0];

                croppingAllocation = croppingAnalysis ? croppingAnalysis.allocations.filter(l => l.getId() === f.croppingId)[0] : null;
            }

            var data = <IAnalysisLayerData>{
                analysis: croppingAnalysis,
                field: f
            };

            if (croppingAllocation) {
                croppingAllocation.layerData = data;
                croppingAllocation.shown = true;
            }

            return data;

        });

        this.setWatchSelection(features);
    }

}
